import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, ThumbsUp, ThumbsDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useMember } from "../context/MemberContext";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "az önce";
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
  return new Date(iso).toLocaleDateString("tr-TR");
};

export const MemberBell = ({ light = false }) => {
  const { token } = useMember();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const known = useRef(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = () => axios.get(`${API}/member/notifications`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data: d }) => {
      if (!alive) return;
      if (known.current) {
        d.items.filter((i) => !i.seen && !known.current.has(i.id)).slice(0, 3).forEach((i) =>
          toast({ title: i.status === "yes" ? "Yeni katılım yanıtı" : "Yeni yanıt", description: i.text }));
      }
      known.current = new Set(d.items.map((i) => i.id));
      setData(d);
    }).catch(() => {});
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [token, toast]);

  const markRead = () => {
    if (!data.unread) return;
    axios.post(`${API}/member/notifications/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setData((d) => ({ ...d, unread: 0 }));
  };

  return (
    <Popover onOpenChange={(o) => !o && markRead()}>
      <PopoverTrigger asChild>
        <button data-testid="member-bell" aria-label="Bildirimler" className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${light ? "text-white hover:bg-white/15" : "text-[#9E2A3B] hover:bg-[#FBEEF0]"}`}>
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span data-testid="member-bell-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#9E2A3B] text-white text-[10px] font-bold flex items-center justify-center">{data.unread > 9 ? "9+" : data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 border-[#F0DDE0]" data-testid="member-bell-panel">
        <div className="px-4 py-3 border-b border-[#F6ECEE] flex items-center justify-between">
          <p className="text-sm font-semibold text-[#2A2224]">Davetiye Yanıtları</p>
          <button onClick={() => navigate("/araclar?arac=web-sitem")} className="text-xs text-[#9E2A3B] hover:underline" data-testid="member-bell-all">Tümünü gör</button>
        </div>
        {!data.items.length ? (
          <p className="text-sm text-[#a8999b] text-center py-8 px-4">Henüz yanıt yok. Davetiyenizi gönderdiğinizde yanıtlar burada görünür.</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {data.items.map((i) => (
              <li key={i.id} className={`flex gap-3 px-4 py-3 border-b border-[#F6ECEE] last:border-0 ${i.seen ? "" : "bg-[#FBF5F0]"}`} data-testid={`member-bell-item-${i.id}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${i.status === "yes" ? "bg-[#E6F4EA] text-[#2f7d4f]" : "bg-[#FBEAEA] text-[#C0392B]"}`}>
                  {i.status === "yes" ? <ThumbsUp className="w-4 h-4" /> : <ThumbsDown className="w-4 h-4" />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-[#2A2224]">{i.text}</p>
                  {i.message && <p className="text-xs text-[#7C6E70] truncate">"{i.message}"</p>}
                  <p className="text-[11px] text-[#a8999b] mt-0.5">{ago(i.at)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};
